import { Injectable } from '@angular/core';
import {ResultListApiModel} from "../../api/models/result-list.api.model";
import {MarketplaceAdvertisementApiModel} from "../../api/models/marketplace-advertisement.api.model";
import {UserViewModel} from "../../features/user/settings/user.view-model";
import {QueryParamsApiModel} from "../../api/models/query-params-api.model";
import {MarketplaceAdvertisementRestService} from "../../api/services/marketplace-advertisement.rest.service";
import {UserRestService} from "../../api/services/user.rest.service";
import {mergeMap, Observable, of} from "rxjs";
import {FollowApiModel} from "../../api/models/follow.api-model";
import {FilterParameterApiModel} from "../../api/models/filter-parameter.api.model";
import {map, tap} from "rxjs/operators";
import {plainToClass} from "class-transformer";
import {HttpHeaders, HttpResponse} from "@angular/common/http";
import {FollowerRestService} from "../../api/services/follower.rest.service";
import {Share} from "@capacitor/share";
import {TranslateService} from "@ngx-translate/core";
import {Router} from "@angular/router";
import {CurrencyPipe} from "@angular/common";
import {TruncateOnWordPipe} from "../../core/pipes/truncate-on-word.pipe";
import {TokenDataProviderService} from "../../core/services/token-data-provider.service";
import {PublicResourceUrlService} from "../services/public-resource-url.service";

@Injectable({
  providedIn: 'root'
})
export class OffersDataProviderService {

  public savedOffers: MarketplaceAdvertisementApiModel[] = [];
  public totalItems: number = 0;
  private savedOffersDirty = false;
  constructor(
    private readonly marketplaceAdvertisementRS: MarketplaceAdvertisementRestService,
    private readonly userRS: UserRestService,
    private readonly followerRS: FollowerRestService,
    private readonly translate: TranslateService,
    private readonly router: Router,
    private readonly currencyPipe: CurrencyPipe,
    private readonly tokenDataProvider: TokenDataProviderService,
    private readonly publicResourceUrlService: PublicResourceUrlService
  ) { }


  public getCollection(queryParams: QueryParamsApiModel = new QueryParamsApiModel(), onlySaved: boolean, owner?: UserViewModel): Observable<ResultListApiModel<MarketplaceAdvertisementApiModel>> {
    let res: Observable<ResultListApiModel<MarketplaceAdvertisementApiModel>>;
    if (owner && onlySaved) {
      res = this.getSavedMarketplaceAdvertisements(queryParams, owner);
    } else if (owner) {
      res = this.userRS.getMarketplaceAdvertisementsSubResourceCollection(owner['@id'], queryParams);
    } else {
      res = this.marketplaceAdvertisementRS.getCollection(queryParams)
    }

    return res;
  }

  public getCollectionByCursor(cursor: string): Observable<ResultListApiModel<MarketplaceAdvertisementApiModel>> {
    return this.marketplaceAdvertisementRS.getCollectionByCursor(cursor);
  }

  public getSavedMarketplaceAdvertisements(queryParams: QueryParamsApiModel = new QueryParamsApiModel(), owner: UserViewModel): Observable<ResultListApiModel<MarketplaceAdvertisementApiModel>> {
    if (!owner || !owner['@id']) {
      return of(new ResultListApiModel<MarketplaceAdvertisementApiModel>(MarketplaceAdvertisementApiModel));
    }
    const refreshHeaders = this.savedOffersDirty ? new HttpHeaders({'x-cache-refresh': '1'}) : undefined;
    return this.userRS.getSavedMarketplaceAdvertisementsSubResourceCollection(owner['@id'], queryParams, {headers: refreshHeaders})
      .pipe(
        tap(() => {
          this.savedOffersDirty = false;
        }),
        mergeMap((resultListAM: ResultListApiModel<FollowApiModel>) => {
      resultListAM.records.forEach((item: FollowApiModel) => {
        const slug = (item.followed as unknown as string).replace('/api/marketplace_advertisements/', '');
        queryParams.filters.push(new FilterParameterApiModel('slug', slug, 'array'));
      });

      if (!queryParams.filters.length) {
        return of(new ResultListApiModel<MarketplaceAdvertisementApiModel>(MarketplaceAdvertisementApiModel));
      }

      return this.marketplaceAdvertisementRS.getCollection(queryParams).pipe(map((marketplaceAdvertisementResultListAM) => {
        marketplaceAdvertisementResultListAM.records.forEach((offer) => {
          offer.currentUserFollowing = resultListAM.records.find(item => item.followed as unknown as string === offer['@id']);
        })
        return marketplaceAdvertisementResultListAM;
      }));
    }));
  }

  public postFollow(offer: MarketplaceAdvertisementApiModel): void {
    this.savedOffersDirty = true;
    const tags = this.mergeTags(
      ['follows', 'marketplace_advertisements', 'saved_marketplace_advertisements'],
      this.buildOfferTags(offer),
      this.buildUserTags()
    );
    this.followerRS.create(
      plainToClass(FollowApiModel, {followed: offer['@id'], type: 'MARKETPLACE_ADVERTISEMENT'}),
      this.buildInvalidateOptions(tags)
    )
      .pipe(map((followAM: FollowApiModel) => {
        offer.currentUserFollowing = followAM;
        this.savedOffers = [...this.savedOffers, offer];
        return followAM;
      })).subscribe();
  }

  public removeFollow(savedOffer: MarketplaceAdvertisementApiModel): void {
    const followAM = savedOffer.currentUserFollowing;
    this.savedOffersDirty = true;
    const tags = this.mergeTags(
      ['follows', 'marketplace_advertisements', 'saved_marketplace_advertisements'],
      this.buildOfferTags(savedOffer),
      this.buildUserTags()
    );
    this.followerRS.delete(savedOffer.currentUserFollowing, this.buildInvalidateOptions(tags)).pipe(map((e: HttpResponse<any>) => {
      savedOffer.currentUserFollowing = null;
      this.savedOffers = this.savedOffers.filter((x: MarketplaceAdvertisementApiModel) => x['@id'] !== (followAM.followed['@id'] || followAM.followed));
      return null;
    })).subscribe();
  }

  public toggleFollow(offer: Record<string, any>, $event: Event): void {
    $event.preventDefault();
    $event.stopPropagation();
    if (!this.tokenDataProvider.isLoggedIn()) {
      this.router.navigate(['/login'], {
        queryParams: {
          returnUrl: this.router.url
        }
      });
      return;
    }
    const savedOffer = this.savedOffers.find( e => e['@id'] === offer['@id']);
    if (!savedOffer) {
      this.postFollow(offer as MarketplaceAdvertisementApiModel);
    } else {
      this.removeFollow(savedOffer);
    }
  }

  async share(offer: Record<string, any>, $event: Event): Promise<void> {
    $event.preventDefault();
    $event.stopPropagation();
    const offerAM = offer as MarketplaceAdvertisementApiModel;
    await Share.share({
      title: offerAM.title,
      text: this.currencyPipe.transform(offerAM.priceGross, offerAM.currency,'code', '1.0') + '.' + TruncateOnWordPipe.truncateOnWord(offerAM.description, 255, offerAM.description.length > 255 ? '...' : ''),
      url: this.getOfferUrl(offerAM),
      dialogTitle: this.translate.instant('common.share'),
    });
  }

  getOfferUrl(offerAM : MarketplaceAdvertisementApiModel): string {
    return this.publicResourceUrlService.getOfferUrl(offerAM.slug || offerAM.id);
  }

  private buildOfferTags(offer: MarketplaceAdvertisementApiModel): string[] {
    const tags: string[] = [];
    if (offer?.id) {
      tags.push(`marketplace_advertisements:${offer.id}`);
    }
    if (offer?.slug) {
      tags.push(`marketplace_advertisements:${offer.slug}`);
    }
    return tags;
  }

  private buildUserTags(): string[] {
    const user = this.tokenDataProvider.getJwtUser();
    if (!user?.id && !user?.slug) {
      return [];
    }
    const tags: string[] = [];
    if (user?.id) {
      tags.push(`users:${user.id}`, `users:${user.id}:saved_marketplace_advertisements`);
    }
    if (user?.slug) {
      tags.push(`users:${user.slug}`, `users:${user.slug}:saved_marketplace_advertisements`);
    }
    return tags;
  }

  private buildInvalidateOptions(tags: string[]): {headers: HttpHeaders} | undefined {
    if (!tags.length) {
      return undefined;
    }
    return {
      headers: new HttpHeaders({'x-cache-invalidate': tags.join(',')})
    };
  }

  private mergeTags(...tagGroups: string[][]): string[] {
    const tags: string[] = [];
    tagGroups.forEach((group) => {
      group.forEach((tag) => {
        if (tag && !tags.includes(tag)) {
          tags.push(tag);
        }
      });
    });
    return tags;
  }
}
